import React from "react";
import usePageTitle from "../hooks/usePageTitle";

const TermsAndConditions = () => {
  usePageTitle("Terms and Conditions - Agrolinka");

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <div className="bg-white rounded-lg shadow-sm p-6 md:p-8">
        <h1 className="text-3xl font-bold text-agBlack mb-2">
          Terms and Conditions
        </h1>
        <p className="text-sm text-agLightGray mb-8">Last updated: 21/1/26</p>

        <div className="space-y-6 text-gray-700">
          <p>
            Welcome to Agrolinka. These Terms and Conditions ("Terms") govern
            your use of our website and the purchase of products from Agrolinka
            (also referred to as "we", "us", "our") at{" "}
            <a
              href="https://agrolinka.com/"
              className="text-agGreen hover:underline"
            >
              https://agrolinka.com/
            </a>
            . By accessing or using the Site, you agree to be bound by these
            Terms. If you do not agree with any part of these Terms, you must
            not use our website or services.
          </p>

          {/* Section 1 */}
          <section>
            <h2 className="text-xl font-semibold text-agBlack mb-3">
              1. ABOUT US
            </h2>
            <p>
              Agrolinka is an online seller of agricultural produce and packaged
              dried food products. We operate as a direct seller, supplying
              goods to customers locally and internationally. Agrolinka is
              registered in Nigeria under RC 1707734 and has its principal place
              of business at 13 Akure Street, War College Estate, Gwarimpa
              Abuja.
            </p>
          </section>

          {/* Section 2 */}
          <section>
            <h2 className="text-xl font-semibold text-agBlack mb-3">
              2. ACCEPTANCE OF TERMS
            </h2>
            <p>
              By using this website and placing orders with us, you confirm that
              you accept these Terms and agree to comply with them. These Terms
              also apply to any purchases you make through the Site.
            </p>
          </section>

          {/* Section 3 */}
          <section>
            <h2 className="text-xl font-semibold text-agBlack mb-3">
              3. ORDERS AND CONTRACT FORMATION
            </h2>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>
                All orders made through the Site are offers by you to purchase
                the products listed.
              </li>
              <li>
                A contract between you and Agrolinka is formed only when we
                accept your order and send you an order confirmation.
              </li>
              <li>
                Prices displayed include applicable taxes unless stated
                otherwise, and are quoted in the currency shown on the Site.
              </li>
            </ul>
          </section>

          {/* Section 4 */}
          <section>
            <h2 className="text-xl font-semibold text-agBlack mb-3">
              4. PAYMENT TERMS
            </h2>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>
                We accept the payment methods displayed at checkout (including
                local and international cards where enabled).
              </li>
              <li>
                Payment must be received in full before your order is processed
                and shipped.
              </li>
              <li>
                You agree that all information you provide for payments is
                correct and that you are authorized to use the payment method
                selected.
              </li>
            </ul>
          </section>

          {/* Section 5 */}
          <section>
            <h2 className="text-xl font-semibold text-agBlack mb-3">
              5. DELIVERY & SHIPPING
            </h2>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>
                We will deliver products to the address you provide during
                checkout.
              </li>
              <li>
                Delivery times may vary depending on your location and product
                availability.
              </li>
              <li>
                Title and risk of loss pass to you upon delivery of the
                products.
              </li>
            </ul>
          </section>

          {/* Section 6 */}
          <section>
            <h2 className="text-xl font-semibold text-agBlack mb-3">
              6. RETURNS & REFUNDS
            </h2>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>
                Please review our Refund Policy for details about returns,
                refunds, and cancellations.
              </li>
              <li>
                Refunds are processed in accordance with that policy and any
                statutory consumer protection laws that apply.
              </li>
            </ul>
          </section>

          {/* Section 7 */}
          <section>
            <h2 className="text-xl font-semibold text-agBlack mb-3">
              7. LIMITATION OF LIABILITY
            </h2>
            <p>
              To the fullest extent permitted by law, Agrolinka will not be
              liable for any indirect, incidental, special, or consequential
              damages resulting from your use of the Site or products purchased.
            </p>
          </section>

          {/* Section 8 */}
          <section>
            <h2 className="text-xl font-semibold text-agBlack mb-3">
              8. INTELLECTUAL PROPERTY
            </h2>
            <p>
              All content on the Site — including logos, text, graphics, images,
              and software — is owned by or licensed to Agrolinka. You may not
              use any intellectual property without our prior written consent.
            </p>
          </section>

          {/* Section 9 */}
          <section>
            <h2 className="text-xl font-semibold text-agBlack mb-3">
              9. USER ACCOUNT
            </h2>
            <p>
              If you create an account with us, you are responsible for
              maintaining the confidentiality of your login details and for any
              activity that occurs under your account.
            </p>
          </section>

          {/* Section 10 */}
          <section>
            <h2 className="text-xl font-semibold text-agBlack mb-3">
              10. CHANGES TO TERMS
            </h2>
            <p>
              Agrolinka reserves the right to modify these Terms at any time.
              Changes take effect when posted on the Site. Your continued use of
              the Site after changes indicates your acceptance of the updated
              Terms.
            </p>
          </section>

          {/* Section 11 */}
          <section>
            <h2 className="text-xl font-semibold text-agBlack mb-3">
              11. GOVERNING LAW
            </h2>
            <p>
              These Terms are governed by the laws of Nigeria. Any disputes will
              be subject to the exclusive jurisdiction of the courts in Nigeria.
            </p>
          </section>

          {/* Section 12 */}
          <section>
            <h2 className="text-xl font-semibold text-agBlack mb-3">
              12. CONTACT DETAILS
            </h2>
            <p className="mb-3">
              If you have any questions about these Terms, please contact us at:
            </p>
            <div className="bg-agBg p-4 rounded-md space-y-2">
              <p>
                <strong>📧 Email:</strong>{" "}
                <a
                  href="mailto:info@agrolinka.com"
                  className="text-agGreen hover:underline"
                >
                  info@agrolinka.com
                </a>
              </p>
              <p>
                <strong>📞 Phone:</strong>{" "}
                <a
                  href="tel:+2348065165064"
                  className="text-agGreen hover:underline"
                >
                  +234 806 516 5064
                </a>
              </p>
              <p>
                <strong>🏢 Address:</strong> 13 Akure Street, War College
                Estate, Gwarimpa, Abuja
              </p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default TermsAndConditions;
